<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            @cancel="close"
            @close="close"
            @hidden="hidden"
        >

            <template #modal-title>
                Import Users CSV
            </template>

            <div class="mb-3">
                <p class="text-muted mb-2">
                    Choose a CSV file. The header row must be exactly these {{ required_csv_headers.length }} columns in this order: {{ required_csv_headers.join(', ') }}.
                    Roles and SKUs assigned values should be semicolon-delimited when a user has more than one.
                    Every data row must have exactly {{ required_csv_headers.length }} comma-separated fields.
                </p>
                <b-form-file
                    ref="usersCsvFormFile"
                    accept=".csv,text/csv,text/plain"
                    placeholder="Choose a CSV…"
                    drop-placeholder="Drop CSV here…"
                    @click.native="onCsvFormFileActivatorClick"
                    @input="onFileChosen"
                />
                <b-alert v-if="csv_parse_error" variant="danger" show class="mt-3 mb-0">
                    {{ csv_parse_error }}
                </b-alert>
                <div
                    v-if="csv_file_attempted"
                    class="mt-3"
                >
                    <div class="mb-2">
                        <strong>{{ parsed_rows.length }} row(s) found</strong>
                    </div>
                    <b-table
                        v-if="preview_fields.length"
                        :items="parsed_rows"
                        :fields="preview_fields"
                        small
                        striped
                        responsive
                        show-empty
                        empty-text="No rows to preview."
                        sticky-header="60vh"
                    />
                </div>
            </div>
            <template #modal-footer>
                <b-button variant="light" @click="close">
                    Close
                </b-button>
                <b-button
                    class="ml-3"
                    variant="success"
                    :disabled="Boolean(csv_parse_error) || !parsed_rows.length || importing_users"
                    @click="importUsers"
                >
                    <b-spinner v-if="importing_users" small class="mr-1" />
                    Import Users
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { parse } from 'csv-parse/dist/cjs/sync.cjs'

const REQUIRED_CSV_HEADERS = Object.freeze([
    'Email',
    'First Name',
    'Last Name',
    'Password',
    'Roles',
    'SKUs assigned'
])

const REQUIRED_COLUMN_COUNT = REQUIRED_CSV_HEADERS.length

export default {
    name: 'UsersImportCsvModal',
    data: () => ({
        csv_parse_error: '',
        csv_file_attempted: false,
        parsed_rows: [],
        importing_users: false
    }),
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        required_csv_headers () {
            return REQUIRED_CSV_HEADERS
        },
        modal_id () {
            return 'users-import-csv-modal'
        },
        preview_fields () {
            const first = this.parsed_rows[0]
            if (!first || typeof first !== 'object') {
                if (!this.csv_file_attempted) {
                    return []
                }
                return REQUIRED_CSV_HEADERS.map(key => ({
                    key,
                    sortable: true
                }))
            }
            return Object.keys(first).map(key => ({
                key,
                sortable: true
            }))
        }
    },
    watch: {
        value (visible) {
            if (visible) {
                this.resetParseState()
            }
        }
    },
    methods: {
        resetParseState () {
            this.csv_parse_error = ''
            this.csv_file_attempted = false
            this.parsed_rows = []
            this.importing_users = false
        },
        /** Clears validation error and resets the picker so picking the same path (e.g. after editing on disk) still fires input. */
        onCsvFormFileActivatorClick () {
            this.csv_parse_error = ''
            const cmp = this.$refs.usersCsvFormFile
            if (cmp && typeof cmp.reset === 'function') {
                cmp.reset()
            }
        },
        onFileChosen (files) {
            const file = Array.isArray(files) ? files[0] : files
            this.csv_parse_error = ''
            this.csv_file_attempted = false
            this.parsed_rows = []

            if (!file) {
                return
            }

            this.csv_file_attempted = true
            const reader = new FileReader()
            reader.onload = (e) => {
                const text = e.target && typeof e.target.result === 'string' ? e.target.result : ''
                this.parseCsvText(text)
            }
            reader.onerror = () => {
                this.csv_parse_error = 'Could not read the selected file.'
            }
            reader.readAsText(file)
        },
        parseCsvText (text) {
            try {
                const rows = parse(text, {
                    columns: false,
                    skip_empty_lines: true,
                    trim: true,
                    relax_column_count: true
                })

                if (!rows.length) {
                    this.csv_parse_error = 'The file contained no data rows.'
                    return
                }
                if (rows.length < 2) {
                    this.csv_parse_error = 'The file contained no data rows.'
                    return
                }

                const headerRow = rows[0]
                const headerSet = new Set(headerRow)
                const missingHeaders = REQUIRED_CSV_HEADERS.filter(h => !headerSet.has(h))
                if (headerRow.length !== REQUIRED_COLUMN_COUNT) {
                    this.csv_parse_error =
                        `The header row must have exactly ${REQUIRED_COLUMN_COUNT} columns (found ${headerRow.length}). ` +
                        this.formatMissingHeadersMessage(missingHeaders)
                    return
                }
                if (new Set(headerRow).size !== REQUIRED_COLUMN_COUNT) {
                    this.csv_parse_error =
                        'The header row must not contain duplicate column names. ' +
                        this.formatMissingHeadersMessage(missingHeaders)
                    return
                }

                if (missingHeaders.length) {
                    this.csv_parse_error =
                        `The CSV header row must include these columns exactly: ${REQUIRED_CSV_HEADERS.join(', ')}. ` +
                        this.formatMissingHeadersMessage(missingHeaders)
                    return
                }
                const strayHeaders = headerRow.filter(h => !REQUIRED_CSV_HEADERS.includes(h))
                if (strayHeaders.length) {
                    this.csv_parse_error =
                        `The header row may only contain: ${REQUIRED_CSV_HEADERS.join(', ')}. ` +
                        `Unexpected column(s): ${strayHeaders.join(', ')}.`
                    return
                }
                const outOfOrderHeaders = REQUIRED_CSV_HEADERS.filter((h, idx) => headerRow[idx] !== h)
                if (outOfOrderHeaders.length) {
                    this.csv_parse_error =
                        `The CSV header row must be in this exact order: ${REQUIRED_CSV_HEADERS.join(', ')}. `
                    return
                }

                for (let i = 1; i < rows.length; i++) {
                    const len = rows[i].length
                    if (len !== REQUIRED_COLUMN_COUNT) {
                        this.csv_parse_error =
                            `Row ${i + 1} must have exactly ${REQUIRED_COLUMN_COUNT} columns (found ${len}).`
                        return
                    }
                }

                const records = rows.slice(1).map(row => {
                    const obj = {}
                    headerRow.forEach((key, idx) => {
                        obj[key] = row[idx]
                    })
                    return obj
                })

                this.parsed_rows = records
                this.$emit('parsed', { rows: records })
            } catch (err) {
                const message = (err && err.message) ? err.message : String(err)
                this.csv_parse_error = message
            }
        },
        async importUsers () {
            if (this.csv_parse_error || !this.parsed_rows.length || this.importing_users) {
                return
            }

            this.importing_users = true

            try {
                const response = await this.axios.post('/api/admin/user/import_csv', {
                    rows: this.parsed_rows
                })
                const created = response.data && response.data.created !== undefined ? response.data.created : 0

                this.$bvToast.toast(`${created} user(s) created.`, {
                    title: 'Users CSV Import',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 10000,
                    appendToast: true
                })
                this.$emit('imported', { created })
                this.close()
            } catch (err) {
                const message = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : err.message

                this.$bvToast.toast(`Failed to import users: ${message}`, {
                    title: 'Users CSV Import',
                    variant: 'danger',
                    solid: false,
                    autoHideDelay: 10000,
                    appendToast: true
                })
            } finally {
                this.importing_users = false
            }
        },
        formatMissingHeadersMessage (missingHeaders) {
            if (!missingHeaders.length) {
                return 'No required headers are missing.'
            }
            return `Missing header(s): ${missingHeaders.join(', ')}.`
        },
        close () {
            this.$emit('hide')
        },
        hidden () {
            this.$emit('hide')
        }
    }
}
</script>
